/**
 * « Aujourd'hui », vue du bureau : qui fait quoi, où, et qui est absent. Un
 * seul composant remplace « L'équipe aujourd'hui » (direction), « Charge de
 * l'équipe » (régulation) et le résumé « Objectifs du jour » : chaque ligne
 * est une personne, ouvre son intervention du moment (ou la suivante), et dit
 * où en sont les objectifs qu'on lui a donnés aujourd'hui.
 */
import type { ReactNode } from 'react';
import { approxClock } from '@myequip/shared';
import { RecordList, RecordRow } from '../../components/RecordRow.js';
import { UserAvatar } from '../../components/UserAvatar.js';
import { EmptyState, Spinner } from '../../components/ui.js';
import { ErrorState } from '../../components/ui/Feedback.js';
import { Section } from '../../components/ui/Section.js';
import { t } from '../../core/i18n.js';
import type { ObjectivePerson, TeamMember, TeamToday } from './home-data.js';

const typeLabel = (type: string): string => t(`interventions.types.${type}`);

function situation(member: TeamMember): string {
  if (member.status === 'absent') {
    return `Absent · ${member.absence?.title || t(`planning.evenements.${member.absence?.kind ?? 'conge'}`)}`;
  }
  if (member.current) {
    const verb = member.status === 'en_route' ? 'En route vers' : member.current.status === 'en_pause' ? 'En pause chez' : 'Chez';
    return `${verb} ${member.current.customer_name} · ${typeLabel(member.current.type)}`;
  }
  if (member.next) {
    return `${member.late ? 'En retard pour' : 'Ensuite'} ${approxClock(member.next.starts_at, member.next.time_approx_minutes)} · ${member.next.customer_name}`;
  }
  return member.status === 'termine' ? t('accueil.journeeTerminee') : t('accueil.sansIntervention');
}

function objectivesLine(person: ObjectivePerson | undefined): string | undefined {
  if (!person) return undefined;
  const parts = [
    person.counts.tenus ? `${person.counts.tenus} tenu${person.counts.tenus > 1 ? 's' : ''}` : '',
    person.counts.en_cours ? `${person.counts.en_cours} en cours` : '',
    person.counts.bloques ? `${person.counts.bloques} bloqué${person.counts.bloques > 1 ? 's' : ''}` : '',
    person.counts.non_atteints ? `${person.counts.non_atteints} non atteint${person.counts.non_atteints > 1 ? 's' : ''}` : '',
  ].filter(Boolean);
  return parts.length ? `Objectifs : ${parts.join(' · ')}` : undefined;
}

function countLine(member: TeamMember): string | undefined {
  if (member.total === 0) return undefined;
  const visits = `${member.total} intervention${member.total > 1 ? 's' : ''}`;
  return member.done ? `${visits} · ${member.done} terminée${member.done > 1 ? 's' : ''}` : visits;
}

export function TodayTeam(props: {
  query: { data: TeamToday | undefined; isLoading: boolean; error: unknown; refetch: () => unknown };
  objectives: ObjectivePerson[];
  day: string;
}): ReactNode {
  const { data, isLoading, error } = props.query;
  const people = data?.people ?? [];
  const objectives = new Map(props.objectives.map((person) => [person.id, person]));
  // Une personne qui a reçu des objectifs mais n'est pas du terrain apparaît aussi.
  const others = props.objectives.filter((person) => !people.some((member) => member.id === person.id));
  return (
    <Section
      id="aujourdhui"
      title={t('accueil.aujourdhui')}
      flush
      action={{ label: t('accueil.voirLEquipe'), to: `/equipe?date=${props.day}` }}
    >
      {isLoading && !data ? <Spinner variant="section" label="Chargement de l’équipe du jour" /> : null}
      {error && !data ? <ErrorState error={error} onRetry={() => void props.query.refetch()} /> : null}
      {data && people.length === 0 && others.length === 0 ? (
        <EmptyState compact>{t('accueil.aucuneInterventionAujourdhui')}</EmptyState>
      ) : null}
      {people.length + others.length > 0 ? (
        <RecordList label={t('accueil.aujourdhui')}>
          {people.map((member) => {
            const visit = member.current ?? member.next;
            return (
              <li key={member.id}>
                <RecordRow
                  className="home-team__row"
                  {...(visit ? { to: `/interventions/${visit.id}`, state: { from: '/' } } : {})}
                  urgent={member.late}
                  lead={<UserAvatar fullName={member.name} avatarUrl={member.avatar_url} avatarFileId={member.avatar_file_id} />}
                  title={member.name}
                  // « Chez … », « En route vers … » disent déjà l'état : pas de pastille, la ligne
                  // garde sa largeur dans la colonne de droite sur ordinateur.
                  lines={[situation(member), countLine(member), objectivesLine(objectives.get(member.id))]}
                />
              </li>
            );
          })}
          {others.map((person) => (
            <li key={person.id}>
              <RecordRow
                className="home-team__row"
                to="/taches?onglet=equipe"
                lead={<UserAvatar fullName={person.name} />}
                title={person.name}
                lines={[objectivesLine(person)]}
              />
            </li>
          ))}
        </RecordList>
      ) : null}
    </Section>
  );
}
